"use client";

import { useEffect, useRef, useState } from "react";
import { Menu, X } from "lucide-react";
import Logo from "@/assets/logo/icone/svg/system-header.webp";
import { Link, useLocation } from "react-router-dom";
import { useDataFastGoals } from "@/hooks/useDataFastGoals";
import { cn } from "@/lib/utils";
import { buttonClass } from "./ui-tokens";

// A ordem espelha a ordem de rolagem da landing (ver src/landing/page.tsx).
// Item daqui é âncora de seção que existe na home. Ao remover seção da home,
// passe por aqui e pelo rodapé.
const navItems = [
  { name: "Solução", href: "#solucao" },
  { name: "Recursos", href: "#recursos" },
  { name: "Resultados", href: "#depoimentos" },
  { name: "Segurança", href: "#seguranca" },
  { name: "Preços", href: "#precos" },
  { name: "FAQ", href: "#faq" },
];

// O listener de scroll aqui PRECISA continuar { passive: true } — um listener
// de `scroll` não-passivo neste componente foi a causa real de um
// congelamento de rolagem antigo no iOS/WebKit. Passivo + rAF evita a volta.
//
// A navegação do desktop ficava escondida (opacidade 0 + aria-hidden) até a
// primeira rolagem. Saiu: link que existe mas não se vê nem se alcança é
// navegação que não existe, e quem abre a página querendo "Preços" precisava
// rolar para descobrir que havia um atalho.
export default function Header() {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const menuButtonRef = useRef<HTMLButtonElement>(null);
  const { pathname } = useLocation();
  const { trackNavClicked, trackLoginClicked, trackStartClicked } =
    useDataFastGoals();

  const handleLogoClick = (e: React.MouseEvent) => {
    trackNavClicked("Logo");
    if (pathname === "/") {
      e.preventDefault();
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  };

  /**
   * Só a barra de oferta. Ela anuncia a condição comercial, e a resposta certa
   * a um clique nela é a tabela de preços — o CTA ao lado é que leva ao
   * cadastro.
   */
  const scrollToPricing = (e: React.MouseEvent) => {
    trackStartClicked();
    if (pathname !== "/") return;
    e.preventDefault();
    document.getElementById("precos")?.scrollIntoView({ behavior: "smooth" });
  };

  // Menu do celular: fecha com Escape (devolvendo o foco ao botão) e com
  // clique fora do cabeçalho.
  useEffect(() => {
    if (!isMobileMenuOpen) return;
    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      if (!target || !document.body.contains(target)) return;
      if (!target.closest("header")) setIsMobileMenuOpen(false);
    };
    const handleKey = (e: KeyboardEvent) => {
      if (e.key !== "Escape") return;
      setIsMobileMenuOpen(false);
      menuButtonRef.current?.focus();
    };
    document.addEventListener("click", handleClickOutside);
    document.addEventListener("keydown", handleKey);
    return () => {
      document.removeEventListener("click", handleClickOutside);
      document.removeEventListener("keydown", handleKey);
    };
  }, [isMobileMenuOpen]);

  useEffect(() => {
    let ticking = false;
    const handleScroll = () => {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(() => {
        setIsScrolled(window.scrollY > 8);
        ticking = false;
      });
    };
    handleScroll();
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const hrefFor = (href: string) => (pathname === "/" ? href : `/${href}`);

  return (
    <header className="sticky top-0 z-50">
      {/* ── Barra de oferta ───────────────────────────────────────────── */}
      <div className="bg-primary text-primary-foreground">
        <a
          href={hrefFor("#precos")}
          onClick={scrollToPricing}
          className="mx-auto flex max-w-6xl items-center justify-center px-4 py-2 text-xs font-medium hover:underline hover:underline-offset-4 sm:px-6"
        >
          {/* Duas versões da mesma oferta. A longa com `truncate` cortava
              exatamente em "garantia de…" num iPhone de 390px — o pedaço que
              tira o risco da decisão era o que sumia. */}
          <span className="sm:hidden">
            7 dias grátis · cobrança só no 8º dia
          </span>
          <span className="hidden sm:inline">
            7 dias grátis para testar: o cartão é cadastrado agora, mas a
            primeira cobrança só acontece no 8º dia
          </span>
        </a>
      </div>

      {/* ── Navegação ─────────────────────────────────────────────────── */}
      {/* A linha de baixo só aparece depois de rolar: no topo o cabeçalho se
          funde ao hero; rolando, ele precisa se separar do conteúdo que passa
          por baixo. */}
      <div
        className={cn(
          "bg-background/90 backdrop-blur-md transition-shadow duration-200",
          isScrolled || isMobileMenuOpen
            ? "shadow-[0_1px_0_0_var(--lp-hairline)]"
            : "shadow-none",
        )}
      >
        <div className="mx-auto flex h-16 max-w-6xl items-center justify-between gap-6 px-4 sm:px-6">
          <Link
            to="/"
            className="-ml-1 flex shrink-0 items-center gap-2.5 rounded-lg p-1 focus-visible:outline-2 focus-visible:outline-ring"
            onClick={handleLogoClick}
          >
            <img src={Logo} alt="" width={30} height={30} />
            <span className="text-base font-semibold tracking-tight text-foreground">
              Ecclesiato
            </span>
          </Link>

          <nav
            className="hidden items-center gap-1 lg:flex"
            aria-label="Navegação principal"
          >
            {navItems.map((item) => (
              <a
                key={item.name}
                href={hrefFor(item.href)}
                onClick={() => trackNavClicked(item.name)}
                className="rounded-full px-3 py-2 text-sm font-medium text-muted-foreground transition-colors duration-150 hover:bg-accent hover:text-foreground"
              >
                {item.name}
              </a>
            ))}
          </nav>

          <div className="hidden items-center gap-2 lg:flex">
            <Link
              to="/login"
              onClick={trackLoginClicked}
              className="rounded-full px-3 py-2 text-sm font-medium text-muted-foreground transition-colors duration-150 hover:text-foreground"
            >
              Entrar
            </Link>
            {/* O rótulo promete começar o teste, então o destino é o cadastro
                — não a tabela de preços. /register sem `?plan=` recebe quem
                chega sem plano com a escolha. */}
            <Link
              to="/register"
              onClick={trackStartClicked}
              className={buttonClass("primary", "md", "h-10")}
            >
              Testar grátis por 7 dias
            </Link>
          </div>

          <button
            ref={menuButtonRef}
            type="button"
            className="-mr-2 grid size-11 place-items-center rounded-full text-foreground hover:bg-accent lg:hidden"
            onClick={(e) => {
              e.stopPropagation();
              setIsMobileMenuOpen((v) => !v);
            }}
            aria-label={isMobileMenuOpen ? "Fechar menu" : "Abrir menu"}
            aria-expanded={isMobileMenuOpen}
            aria-controls="menu-mobile"
          >
            {isMobileMenuOpen ? (
              <X className="size-5" strokeWidth={1.75} aria-hidden="true" />
            ) : (
              <Menu className="size-5" strokeWidth={1.75} aria-hidden="true" />
            )}
          </button>
        </div>

        {isMobileMenuOpen && (
          <nav
            id="menu-mobile"
            className="lp-open max-h-[calc(100svh-6rem)] overflow-y-auto overscroll-contain px-4 pt-2 pb-6 lg:hidden"
            aria-label="Navegação principal"
          >
            <ul className="flex flex-col">
              {navItems.map((item) => (
                <li key={item.name}>
                  <a
                    href={hrefFor(item.href)}
                    onClick={() => {
                      setIsMobileMenuOpen(false);
                      trackNavClicked(item.name);
                    }}
                    className="flex h-12 items-center text-base font-medium text-foreground"
                  >
                    {item.name}
                  </a>
                </li>
              ))}
            </ul>

            <div className="mt-4 flex flex-col gap-3">
              <Link
                to="/register"
                onClick={() => {
                  setIsMobileMenuOpen(false);
                  trackStartClicked();
                }}
                className={buttonClass("primary", "lg", "w-full")}
              >
                Testar grátis por 7 dias
              </Link>
              <Link
                to="/login"
                onClick={() => {
                  setIsMobileMenuOpen(false);
                  trackLoginClicked();
                }}
                className={buttonClass("secondary", "lg", "w-full")}
              >
                Já tenho conta
              </Link>
              <p className="mt-1 text-center text-xs text-muted-foreground">
                Sem fidelidade · Cancele quando quiser
              </p>
            </div>
          </nav>
        )}
      </div>
    </header>
  );
}
